import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useContext, createContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, Plug, ChevronRight, Menu,
  Database, BarChart3, Activity,
  FileText, Settings as SettingsIcon, Rocket, Package, Zap, LogOut, Cpu, Users, KeyRound, BrainCircuit,
  Gauge, Upload, History, Waypoints,
} from 'lucide-react';
import { getHealth, getEnvironments } from '../api/l1';
import { useAuth } from '../context/AuthContext';
import LicenseStatusBanner from './LicenseStatusBanner';
import monetedgeLogo from '../assets/MonetEdge.svg';
// Aliased -- this file's own `Environment` (below) is the string-tier type; the full catalog
// record shape (name/rank/is_default/description) lives in types/index.ts under the same name.
import type { Environment as EnvironmentRecord } from '../types';

const faviconUrl = '/favicon.svg';
const WORKSPACE_ACTIVE = '#0049AC';
const NAV_ACTIVE = '#3b82f6';
const ENV_KEY = 'rtf_environment';

// ── Workspace definitions ────────────────────────────────────────────────────

// A workspace's nav is a list of SECTIONS, not a flat item list -- lets a workspace group
// related pages under a small uppercase sub-label (see the render loop below) without a second
// restructure every time a new group of pages lands. `label` omitted = no header, items render
// flush under the workspace's own top-level label (matches every nav's original flat look).
interface NavItem { to: string; icon: React.ElementType; label: string }
interface SoonNavItem { icon: React.ElementType; label: string }
// External nav items (Telemetry/Request Flows) render like a NavItem but point at a fully
// external URL (Grafana) rather than an in-app route -- `hrefForEnv` mirrors the existing
// telemetryUrlForEnv() call pattern each link already builds today, just parameterized so the
// render loop can produce both links instead of two separate hardcoded <a> blocks.
interface ExternalNavItem { hrefForEnv: (env: Environment) => string; icon: React.ElementType; label: string }
// `soonItems` renders inline within this same section (below `items`, same label/grouping) --
// e.g. Policy Management belongs with User/Role/Resource management, not lumped into a separate
// page-wide "coming soon" block.
interface NavSection { label?: string; items: NavItem[]; externalItems?: ExternalNavItem[]; soonItems?: SoonNavItem[] }

// Fabric's own (now much narrower) nav -- everything else that used to live here (Audit Trail,
// Settings) moved to COMMON_NAV since those pages aren't workspace-specific. RAM-158: the old
// in-workspace "Dashboard" item (-> /dashboard) was removed -- that route now redirects straight
// to /overview (Platform Dashboard, already reachable via its own dedicated nav entry above the
// workspace switcher), so keeping a workspace-scoped nav item that just bounces you out of the
// workspace to the SAME already-reachable page was pure dead weight, not a real destination.
// Connections is now this workspace's own landing page (also the switcher pill's own target).
const FABRIC_NAV: NavSection[] = [
  { items: [
    { to: '/connections', icon: Plug, label: 'Connections' },
  ] },
];

// Blueprint's own (now much narrower) nav -- Data Access Governance, Policy & Enforcement, and
// Monitoring moved to COMMON_NAV below since they aren't Blueprint-specific (reachable/relevant
// regardless of which workspace pill is active). Usage stayed here (moved back per direct
// feedback, 2026-08-06) -- unlike Data Access Governance (a platform-wide GOVERNANCE concept
// even though today's data is Blueprint-only), Usage is per-product BILLING/METERING: every
// event source feeding it (routes/runtime_proxy.py, routes/mcp_proxy.py, routes/packages.py) is
// Blueprint-only, and its own docstring is literally "one row per (package, billing period)" --
// packages don't exist in Fabric. If Fabric ever gets its own aggregated usage view, it should
// be a separate page, not blended into this one -- same reasoning that keeps Packages/Runtime
// Manager here rather than in COMMON_NAV. (Fabric's own usage-adjacent event ledger,
// runtime_foundation/usage_ledger.py, is surfaced separately via Audit Trail's "Runtime
// Operations" tab, which stays common regardless of this.)
// RAM-158: same removal as FABRIC_NAV above -- /blueprint now redirects to /overview, so the old
// in-workspace "Dashboard" item is gone. Packages is this workspace's own landing page.
const BLUEPRINT_NAV: NavSection[] = [
  { items: [
    { to: '/blueprint/packages',        icon: Package, label: 'Packages'        },
    { to: '/blueprint/runtime-manager', icon: Cpu,     label: 'Runtime Manager' },
    { to: '/blueprint/usage',           icon: Zap,     label: 'Usage'           },
  ] },
];

// Cross-workspace nav -- pages that aren't owned by either Runtime Trust Fabric or Blueprint
// Runtime specifically (identity/governance, usage, audit, monitoring, settings). Always
// rendered regardless of which workspace pill is active or even whether one is active at all
// (see `onWorkspaceSection` below). Routes are UNCHANGED from their pre-move locations (several
// still live under /blueprint/* for historical reasons -- see isBlueprintWorkspaceRoute()).
const COMMON_NAV: NavSection[] = [
  // Principal/role identity management (API_USER_PDP_AUTHORIZATION_DESIGN.md Phase 1+2) --
  // "who is this principal and what broad role do they hold." Resources (what a role's
  // permissions actually target) and Policy Management (how access to them is governed) share
  // this section rather than being split into a second "Policy & Enforcement" section.
  //
  // RAM-185 (2026-08-07): Policy Management is now a REAL page, not a placeholder -- Phase
  // 4/5/6 (data-access profiles, PDP policies/versions/subjects + evaluator, downstream
  // enforcement bindings) plus the RAM-166 boundary layer are all real, live-tested backends
  // (blueprint_runtime/authz/policy_repository.py, profile_repository.py, mapping_repository.py,
  // binding_repository.py, boundary_repository.py + routes/authz_policies.py, authz_profiles.py,
  // authz_bindings.py, authz_boundaries.py). Per this same section's prior forward design
  // guidance (still honored, not reconsidered): policies/profiles/bindings are NOT split into
  // separate disconnected per-table CRUD pages the way Resources ended up isolated from
  // Users/Roles -- PolicyManagement.tsx is ONE guided authoring flow ("Who -> What -> Effect ->
  // Exposure -> Enforcement," Sec 18.1) with a table/list view underneath for bulk ops/search.
  // A real per-decision PDP evaluation/trace view (Sec 34's evidence chain, extending
  // Monitoring's Activity page) is still a separate, later increment -- not built here.
  // The approved API Access redesign (2026-08-20): the old "Data Access Governance" section
  // (User Management, Role Management, Resources, Policy Management, Enforcement Status) is
  // HIDDEN from the nav -- the pages and their App.tsx routes stay URL-reachable during the
  // transition, but admins now manage API USERS and grant them PACKAGE access from the single
  // "API Access" page below; everything inside a package is decided by the imported governance
  // bundle, and roles are deliberately invisible. Connectors (the one survivor of that section)
  // moved into Governance below. "API User Associations" and "Effective Access" are likewise
  // hidden (routes kept) -- API Access replaces the former in the nav.
  { label: 'Governance', items: [
    { to: '/governance/overview',                icon: Gauge,      label: 'Overview' },
    { to: '/governance/import',                   icon: Upload,     label: 'Import Bundle' },
    { to: '/governance/bundle-history',           icon: History,    label: 'Bundle History' },
    { to: '/governance/api-access',               icon: Users,      label: 'API Access' },
    { to: '/governance/access-trace',             icon: Waypoints,  label: 'Access Trace' },
    { to: '/blueprint/api-access/connectors',     icon: Plug,       label: 'Connectors' },
  ] },
  { items: [
    { to: '/audit', icon: FileText, label: 'Audit Trail' },
  ] },
  // Monitoring (P3) -- Activity covers control-plane ADMIN ACTIONS on the authz surface
  // (onboard/suspend/revoke/etc.) -- a real PDP decision trace is a separate, later thing
  // (Phase 5+) that should extend this same page, not become a second Activity page.
  // Telemetry/Request Flows (external Grafana links) now render here via `externalItems`
  // instead of as two standalone hardcoded <a> blocks below the nav sections.
  { label: 'Monitoring', items: [
    { to: '/blueprint/monitoring/activity', icon: Activity, label: 'Activity' },
  ], externalItems: [
    { hrefForEnv: (env: Environment) => telemetryUrlForEnv(TELEMETRY_URL(), env), icon: BarChart3, label: 'Telemetry' },
    { hrefForEnv: (env: Environment) => telemetryUrlForEnv(TELEMETRY_FLOWS_URL(), env), icon: Activity, label: 'Request Flows' },
  ] },
  { label: 'Settings', items: [
    { to: '/settings/environment',    icon: SettingsIcon, label: 'Environment Management' },
    { to: '/settings/llm',            icon: BrainCircuit, label: 'LLM Settings' },
    { to: '/settings/license',        icon: KeyRound,      label: 'License Management' },
    // RAM-181: self-service, reachable regardless of auth_source -- MyApiAccess.tsx itself
    // explains the CIAM-only restriction rather than hiding the nav entry for a "local"
    // bootstrap-admin session (consistent with every other Settings item here having no
    // permission gate of its own either).
    { to: '/settings/my-api-access',  icon: KeyRound,      label: 'My API Access' },
  ] },
];

// ---------------------------------------------------------------------------
// Minimal nav for API-only CIAM sessions (2026-08-20 direct requirement). An invited API-only
// user (Control Plane roles ApiReportReader/ApiMcpInvoker/ApiDataIntegrator) has no dashboard
// access at all -- only "My API Access" and logout make sense for them.
//
// Signal investigated: AuthUser (context/AuthContext.tsx) carries `tenantRole` (the raw CIAM
// tenant-role CODE, e.g. "TenantAdmin"/"DataEngineer"/"ApiDataIntegrator" -- `groups[0]` off the
// ID token, or `data.tenant_role` from POST .../login-tenant) but NO `groups` or `permissions`
// array. The mapping from that role code to local Runtime Suite groups
// (CIAM_TENANT_ROLE_TO_LOCAL_GROUPS: TenantAdmin/DataEngineer/Operations only) lives
// server-side ONLY, in blueprint_runtime/iam/identity_context.py -- resolved per-request from
// the CIAM access token and never returned as part of the login response or ID-token claims.
// So the session object genuinely lacks a `groups`/`permissions` field client-side; the best
// available signal is `tenantRole` itself, checked against the SAME allow-list the backend
// uses (mirrored here, not reinvented) plus "Manager" (RAM-250's `dashboards.all` role, which
// is NOT actually present in identity_context.py's CIAM_TENANT_ROLE_TO_LOCAL_GROUPS today --
// an apparent pre-existing gap between that table and the newer Manager role, flagged here
// rather than silently worked around; Manager is included in this client-side allow-list on
// the strength of its own documented dashboards.all permission, not a guess).
//
// Caveat found while grounding this: today, iam_auth_tenant.py's own login-tenant gate
// (_is_granted, requiring one of dashboard.runtime/dashboard.customer_environment/
// dashboards.all) already blocks EVERY Api*/ReadOnly/BusinessAnalyst tenant login at the sign-in
// screen itself ("Not provisioned for Runtime access") -- such a session never reaches this
// Layout today. This gating is therefore a forward-looking safeguard matching the exact spec
// given (defensive for whenever that upstream gate changes, or for any other path that lands a
// CIAM session with no locally-mapped group here), not something live-verifiable against a real
// ApiDataIntegrator session without also touching that separate login-gate file -- out of this
// change's scope and not done here.
// Exported (2026-08-21) so Login.tsx can route a freshly-authenticated tenant user straight to
// their own real landing page instead of blindly trusting a stale location.state.from -- a minimal-
// nav (self-service-API-only) role has no legitimate reason to land on an admin page a PREVIOUS,
// different person may have been browsing in the same tab before this login. See isMinimalNav's
// own docstring just below for the full reasoning this reuses.
export const ADMIN_TENANT_ROLES = new Set(['TenantAdmin', 'DataEngineer', 'Operations', 'Manager']);

const MINIMAL_NAV: NavSection[] = [
  { label: 'Settings', items: [
    { to: '/settings/my-api-access', icon: KeyRound, label: 'My API Access' },
  ] },
];

// Fabric's own (now much narrower) route territory. Includes /drift and /platform defensively
// even though they're not in FABRIC_NAV today (unlinked legacy routes reachable via inline
// buttons elsewhere) -- landing there should still show Fabric chrome, not none at all.
const FABRIC_OWN_PREFIXES = ['/dashboard', '/connections', '/drift', '/platform'];
// Blueprint's own territory EXCLUDES the common sub-paths under /blueprint/* (api-access,
// monitoring) even though they share the /blueprint/ URL prefix -- that's exactly the collision
// this function exists to resolve. '/blueprint' itself (the dashboard index) is handled as an
// exact match, not a prefix, since it's also a strict prefix of every other /blueprint/* route.
// '/blueprint/usage' IS included here (moved back from COMMON_NAV, see BLUEPRINT_NAV's comment).
const BLUEPRINT_OWN_PREFIXES = ['/blueprint/packages', '/blueprint/runtime-manager', '/blueprint/usage'];

function isFabricWorkspaceRoute(pathname: string): boolean {
  return FABRIC_OWN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}
function isBlueprintWorkspaceRoute(pathname: string): boolean {
  if (pathname === '/blueprint') return true;
  return BLUEPRINT_OWN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

// Grafana UI bundled by this compose's otel-collector service (grafana/otel-lgtm), mapped to
// host 3001 locally (3000 is occupied by an unrelated local process). A deployed environment
// (docker-compose.yml's GF_SERVER_SERVE_FROM_SUB_PATH) instead serves Grafana under this same
// page's own origin at /grafana, routed through the ALB (lb_stack.py's RouteGrafana) exactly like
// every other service -- a hardcoded http://localhost:3001 sent every deployed admin's browser to
// their OWN localhost, and port 3001 was never even reachable from outside the container before
// this fix (found live 2026-08-21 while cross-checking Grafana wiring for the beta deployment).
// Local operators use both localhost and 127.0.0.1. Treat both as the direct-port Docker topology;
// otherwise 127.0.0.1 incorrectly produces /grafana on the UI nginx origin and every Outputs link
// lands on nginx's 404 page. Deployed environments continue to use the same-origin ALB route.
function grafanaBaseUrl(): string {
  const isLocal = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  return isLocal ? `${window.location.protocol}//${window.location.hostname}:3001` : `${window.location.origin}/grafana`;
}
// Cross-cutting observability, not scoped to either workspace, so it's rendered outside
// FABRIC/BLUEPRINT_NAV. Points at the curated "Runtime Suite -- Overview" dashboard (request
// rate/errors/latency + live Loki logs, service-filterable) rather than Grafana's bare root, which
// otherwise drops visitors on the generic first-run welcome/tutorial screen with no indication
// data even exists. Exported so other pages (e.g. AuditTrail's "Runtime Operations" tab) can
// deep-link here instead of duplicating the URL -- keep this the single source of truth for the
// Telemetry destination. A function, not a module-level const, so it re-reads window.location at
// call time rather than baking in whatever origin happened to be current at module-load time.
export const TELEMETRY_URL = () => `${grafanaBaseUrl()}/d/runtime-suite-overview/runtime-suite-overview`;
// Package/environment/resource-scoped request-flow tracing (MCP tool/resource dispatch, RM
// report/dataset execution) -- see runtime_suite/grafana/dashboards/runtime-flows.json. The more
// relevant destination than TELEMETRY_URL above for anything about a SPECIFIC package's traffic,
// as opposed to general cross-service request-rate/error-rate/latency.
export const TELEMETRY_FLOWS_URL = () => `${grafanaBaseUrl()}/d/runtime-suite-flows/runtime-suite-request-flows`;

// The environment catalog is real, backend-managed data (GET/POST/PUT/DELETE /environments,
// runtime_suite/fabric/backend/runtime_packaging_routes/environments.py) with a `rank` column
// that defines promotion order -- it used to be shadowed by this hardcoded 4-value array, which
// meant any custom environment (e.g. 'uat', already created via the API) was invisible here and
// unrankable in BindConnectionsModal's sort. `Environment` is now a plain string rather than a
// literal union of this array's values, since the real set is dynamic; useEnvironments() below
// is the query every consumer should use to read the actual catalog.
export type Environment = string;

export function useEnvironments() {
  return useQuery({
    queryKey: ['environments'],
    queryFn: () => getEnvironments().then((r) => r.data.environments as EnvironmentRecord[]),
    staleTime: 30_000,
  });
}

// A plain CSS `capitalize` transform (the previous approach, when this was a hardcoded 4-value
// array) reads any acronym-shaped name wrong -- 'uat' -> 'Uat' instead of 'UAT'. The catalog is
// now dynamic so this can't be solved by just spelling the value differently in a hardcoded
// list; every acronym-shaped environment name added needs adding here once.
const ACRONYM_ENV_NAMES = new Set(['uat']);
export function formatEnvironmentName(name: string): string {
  if (ACRONYM_ENV_NAMES.has(name.toLowerCase())) return name.toUpperCase();
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// Every page here already scopes its OWN data to the header's selected environment
// (useEnvironment()) -- deep links out to Grafana should carry that same selection instead of
// landing on an unscoped "All" view the user then has to re-filter by hand. Grafana silently
// ignores a var-<name> param a given dashboard doesn't declare (e.g. var-environment against
// runtime-suite-overview, which has no such template variable), so this is safe to apply
// everywhere a TELEMETRY_URL/TELEMETRY_FLOWS_URL link is built, not just against
// runtime-suite-flows.
export function telemetryUrlForEnv(baseUrl: string, env: Environment, extraVars?: Record<string, string>): string {
  const varEntries: [string, string][] = Object.entries(extraVars ?? {}).map(([name, value]) => [`var-${name}`, value]);
  const params = new URLSearchParams([['var-environment', env], ...varEntries]);
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${params.toString()}`;
}

// Deep-links straight into a specific trace's waterfall in Grafana Explore -- for anything that
// already has a concrete trace_id in hand (e.g. an AuditEvent row, stamped via
// get_current_trace_id() at write time) and wants to jump right to it instead of re-discovering
// it via a filtered dashboard. URL shape (schemaVersion=1 "panes" JSON, not the older single
// "left" param) confirmed empirically against this Grafana version by opening a real trace and
// reading back the resulting URL -- Tempo's queryType:"traceql" editor resolves a bare 32-hex
// trace ID as a direct lookup. The 7-day range is just generous padding for Explore's own UI
// state (a time-range param it always requires), not a confirmed server-side filter on the
// lookup itself.
export function traceExploreUrl(traceId: string): string {
  const panes = {
    a: {
      datasource: 'tempo',
      queries: [{ refId: 'A', queryType: 'traceql', query: traceId, datasource: { type: 'tempo', uid: 'tempo' } }],
      range: { from: 'now-7d', to: 'now' },
    },
  };
  const params = new URLSearchParams({ schemaVersion: '1', panes: JSON.stringify(panes), orgId: '1' });
  return `${grafanaBaseUrl()}/explore?${params.toString()}`;
}

// Color-coded by stakes, not just alphabetically -- the environment selector governs which
// connections/data every page acts on, so it needs to be impossible to miss which tier you're
// in (sandbox/dev = safe to experiment, staging = caution, production = real/external/high-stakes).
// Deliberately not exhaustive over every possible name -- the catalog is now dynamic (a custom
// tier like 'uat' has no inherent "stakes" this file can know), so this only covers the 4
// well-known tiers; getEnvColors() below supplies a deterministic fallback for anything else,
// same pattern as Badge.tsx's FALLBACK_PALETTE for custom status/environment tag strings.
const ENV_COLORS: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  sandbox:    { bg: '#f0f9ff', border: '#7dd3fc', text: '#0369a1', dot: '#38bdf8' },
  dev:        { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8', dot: '#3b82f6' },
  staging:    { bg: '#fffbeb', border: '#fbbf24', text: '#b45309', dot: '#f59e0b' },
  production: { bg: '#fef2f2', border: '#f87171', text: '#b91c1c', dot: '#ef4444' },
};
const ENV_COLOR_FALLBACKS: { bg: string; border: string; text: string; dot: string }[] = [
  { bg: '#fdf2f8', border: '#f9a8d4', text: '#9d174d', dot: '#ec4899' },
  { bg: '#f0fdfa', border: '#5eead4', text: '#0f766e', dot: '#14b8a6' },
  { bg: '#faf5ff', border: '#d8b4fe', text: '#7e22ce', dot: '#a855f7' },
];
function getEnvColors(env: string): { bg: string; border: string; text: string; dot: string } {
  if (ENV_COLORS[env]) return ENV_COLORS[env];
  let hash = 0;
  for (let i = 0; i < env.length; i++) hash = (hash * 31 + env.charCodeAt(i)) >>> 0;
  return ENV_COLOR_FALLBACKS[hash % ENV_COLOR_FALLBACKS.length];
}

type Workspace = 'fabric' | 'blueprint';

// Client-side "which environment tier am I pointed at" selection for the whole UI session --
// separate from the per-connection Connection.environment field. Persisted to localStorage;
// defaults to 'sandbox' absent a prior selection. A Context (not a plain hook) because every
// page needs to react to the SAME selection -- a plain useState-based hook would give each
// caller (Layout's selector, Usage.tsx's query) its own independent, non-communicating state.
const EnvironmentContext = createContext<[Environment, (env: Environment) => void] | null>(null);

function useEnvironmentState(): [Environment, (env: Environment) => void] {
  const [env, setEnv] = useState<Environment>(
    () => (localStorage.getItem(ENV_KEY) as Environment) || 'sandbox',
  );
  const setEnvironment = (next: Environment) => {
    localStorage.setItem(ENV_KEY, next);
    setEnv(next);
  };
  return [env, setEnvironment];
}

export function useEnvironment(): [Environment, (env: Environment) => void] {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error('useEnvironment() must be used within Layout (EnvironmentContext.Provider)');
  return ctx;
}

function SoonItem({ icon: Icon, label, collapsed }: { icon: React.ElementType; label: string; collapsed: boolean }) {
  return (
    <div
      title={collapsed ? `${label} (not yet available)` : undefined}
      style={{
        display: 'flex', alignItems: 'center',
        gap: collapsed ? 0 : 8,
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: '6px 10px', borderRadius: 6,
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
        color: '#cbd5e1', cursor: 'default', userSelect: 'none',
      }}
    >
      <Icon size={14} />
      {!collapsed && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {label}
          <span style={{ fontSize: 9, padding: '1px 4px', background: '#f1f5f9', color: '#94a3b8', borderRadius: 4, fontWeight: 600 }}>Soon</span>
        </span>
      )}
    </div>
  );
}

// Shared section-render block -- used identically for both the active workspace's own nav
// (FABRIC_NAV/BLUEPRINT_NAV) and COMMON_NAV, so a future NavSection-shape change (like this
// task's own `externalItems` addition) only needs to be made once instead of drifting between
// two copies.
function NavSectionBlock({ section, sectionIndex, collapsed, env }: {
  section: NavSection; sectionIndex: number; collapsed: boolean; env: Environment;
}) {
  return (
    <div key={section.label ?? `section-${sectionIndex}`} style={{ marginTop: sectionIndex > 0 ? 10 : 0 }}>
      {section.label && !collapsed && (
        <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#cbd5e1', padding: '0 10px 3px' }}>
          {section.label}
        </p>
      )}
      {section.items.map(({ to, icon: Icon, label }) => (
        <NavLink key={to} to={to} title={collapsed ? label : undefined}
          // Dashboard's path ('/dashboard' or '/blueprint') is a prefix of every sibling
          // route in this list (e.g. '/blueprint/runtime-manager') -- NavLink's default
          // isActive matching is prefix-based, so without `end` it stayed highlighted on
          // every sub-page at once. Same index-route distinction the breadcrumb `crumb`
          // logic above already makes.
          end={to === '/dashboard' || to === '/blueprint'}
          style={({ isActive }) => ({
            display: 'flex', alignItems: 'center',
            gap: collapsed ? 0 : 8, justifyContent: collapsed ? 'center' : 'flex-start',
            padding: '7px 10px', borderRadius: 6, textDecoration: 'none',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
            transition: 'all 0.15s',
            backgroundColor: isActive ? NAV_ACTIVE : 'transparent',
            color: isActive ? '#fff' : '#475569',
          })}
        >
          <Icon size={14} />
          {!collapsed && <span>{label}</span>}
        </NavLink>
      ))}
      {/* External links (Telemetry/Request Flows) -- same styling/behavior as an in-app NavLink
          item, but pointing at a fully external URL. Explicit window.open() + preventDefault
          rather than relying on target="_blank" alone -- plain href/target navigated this tab
          away to Grafana instead of only opening a new one. */}
      {section.externalItems?.map(({ hrefForEnv, icon: Icon, label }) => (
        <a
          key={label}
          href={hrefForEnv(env)} target="_blank" rel="noopener noreferrer"
          onClick={(e) => { e.preventDefault(); window.open(hrefForEnv(env), '_blank', 'noopener,noreferrer'); }}
          title={collapsed ? label : undefined}
          style={{
            display: 'flex', alignItems: 'center',
            gap: collapsed ? 0 : 8, justifyContent: collapsed ? 'center' : 'flex-start',
            padding: '7px 10px', borderRadius: 6, textDecoration: 'none',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
            color: '#475569',
          }}
        >
          <Icon size={14} />
          {!collapsed && <span>{label}</span>}
        </a>
      ))}
      {section.soonItems?.map(({ icon, label }) => (
        <SoonItem key={label} icon={icon} label={label} collapsed={collapsed} />
      ))}
    </div>
  );
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace>('fabric');
  const [env, setEnvironment] = useEnvironmentState();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { data: environments } = useEnvironments();
  const envColors = getEnvColors(env);
  // See MINIMAL_NAV's own comment above for the full signal investigation. A "local"
  // bootstrap-admin session is never gated (no tenantRole concept applies to it at all --
  // mirrors MyApiAccess.tsx's own isCiam-only precedent).
  const isMinimalNav = user?.authSource === 'ciam' && !ADMIN_TENANT_ROLES.has(user.tenantRole ?? '');

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  useEffect(() => {
    if (isBlueprintWorkspaceRoute(location.pathname)) setWorkspace('blueprint');
    else if (isFabricWorkspaceRoute(location.pathname)) setWorkspace('fabric');
    // else: on Platform Dashboard or a common-section page -- deliberately DON'T change
    // `workspace` state here, so it still remembers "which workspace was I last in" for the
    // pill's own default navigate-target if clicked next, without forcing a switch just because
    // you browsed to a common page. This mirrors today's implicit behavior, generalized with one
    // more case.
  }, [location.pathname]);

  const { data } = useQuery({
    queryKey: ['health'],
    queryFn: () => getHealth().then((r) => r.data),
    refetchInterval: 30_000,
  });

  const nav = workspace === 'fabric' ? FABRIC_NAV : BLUEPRINT_NAV;
  // '/overview' (Platform Dashboard) is workspace-agnostic chrome, not owned by either
  // workspace -- shows its own label with no second breadcrumb segment, same "no workspace
  // prefix" convention confirmed for this page's subtitle, rather than inheriting whichever
  // workspace pill happens to be highlighted (which is itself just the path-derived default,
  // not a real "you are in this workspace" fact while on this page).
  const onPlatformDashboard = location.pathname === '/overview';
  // True while the current route is actually inside Fabric's or Blueprint's own (narrow) route
  // set -- false on Platform Dashboard AND on any COMMON_NAV page, even though the latter may
  // still share a /blueprint/* URL prefix with Blueprint's own routes (see
  // isBlueprintWorkspaceRoute()'s exclusion of those common sub-paths). Drives every place that
  // used to gate on `!onPlatformDashboard` for "is a workspace pill actually active" --
  // generalizes that two-state check into the new three-state (platform / workspace / common) model.
  const onWorkspaceSection = isFabricWorkspaceRoute(location.pathname) || isBlueprintWorkspaceRoute(location.pathname);
  // Two distinct labels, deliberately not one shared variable: `workspaceLabel` names
  // whichever workspace's nav items are actually listed below in the sidebar (still
  // path-derived `workspace` state even while on /overview, since that state doesn't change
  // just because the platform-wide page is showing -- see the nav-array rendering below,
  // unaffected by this page). `headerLabel` is ONLY the header breadcrumb's own text, which
  // DOES get overrides for the Platform Dashboard and common-page cases below.
  const workspaceLabel = workspace === 'fabric' ? 'Runtime Trust Fabric' : 'Blueprint Runtime';

  const allNav = [...FABRIC_NAV, ...BLUEPRINT_NAV, ...COMMON_NAV].flatMap((section) => section.items);
  // Resolve the most specific matching nav entry for the breadcrumb
  const crumb = onPlatformDashboard ? '' : [...allNav].sort((a, b) => b.to.length - a.to.length).find((n) => {
    if (n.to === '/dashboard' || n.to === '/blueprint') return location.pathname === n.to;
    return location.pathname.startsWith(n.to);
  })?.label ?? (location.pathname === '/blueprint/packages/import' ? 'Import Package' : '');

  // On a COMMON_NAV page (not Platform Dashboard, not inside either workspace's own route set),
  // prefer that section's own label (e.g. "Data Access Governance", "Monitoring", "Settings")
  // over the last-active workspace label. Sections with no `label` (Usage, Audit Trail -- single
  // flush items) fall back to `crumb` itself so the header doesn't render a redundant
  // "Usage / Usage" -- see the `crumb !== headerLabel` guard where this is rendered below.
  const matchedCommonSection = COMMON_NAV.find((s) =>
    s.items.some((i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'))
  );
  const headerLabel = onPlatformDashboard
    ? 'Platform Dashboard'
    : onWorkspaceSection
    ? workspaceLabel
    : (matchedCommonSection?.label ?? crumb ?? workspaceLabel);

  const sidebarW = collapsed ? 60 : 228;

  return (
    <EnvironmentContext.Provider value={[env, setEnvironment]}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'fixed', width: '100%', top: 0, left: 0 }}>

      {/* ── Header ── styled to match the control-plane UI's Header.tsx (me-ui-staging/
          src/pages/Tenant/components/Header.tsx): 92px tall, bordered brand box on the
          left, centered title+breadcrumb, bordered utility cluster on the right --
          adapted here to carry Runtime Suite's own functional elements (env selector,
          connection/license status, user+logout) in that right box instead of the
          control plane's Info/Settings icon pair. ── */}
      <header style={{
        height: 92, flexShrink: 0, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0',
        display: 'flex', alignItems: 'center', zIndex: 1050, overflow: 'hidden',
      }}>
        {/* Left: brand mark, at a FIXED width regardless of sidebar collapse state -- matching
            the control plane's own header, where the logo box and the collapsible nav rail are
            independent elements (the rail's icons-only state doesn't shrink the header logo).
            Runtime Suite's sidebar<->header width-sync was the bug: collapsing to icons was
            squeezing this box down to 60px and forcing the logo to shrink with it. */}
        <div style={{
          width: 228, flexShrink: 0, height: '100%', borderRight: '1px solid #e0e0e0',
          display: 'flex', flexDirection: 'row', alignItems: 'center',
          justifyContent: 'center', gap: 8,
        }}>
          <button onClick={() => navigate('/overview')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
            title="Go to Platform Dashboard"
          >
            <img
              src={monetedgeLogo}
              alt="MonetEdge"
              style={{ height: 30, width: 'auto', objectFit: 'contain', maxWidth: 130 }}
            />
          </button>
        </div>

        {/* Center: brand title + page breadcrumb, mirroring "MonetEdge Studio" / category-path */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 0, maxWidth: '100%', padding: '0 12px' }}>
          <h1
            onClick={() => navigate('/overview')}
            style={{
              margin: 0, fontSize: 26, fontWeight: 700, color: WORKSPACE_ACTIVE, lineHeight: 1.2,
              cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis', maxWidth: '100%',
            }}
          >
            Runtime Suite
          </h1>
          <div style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: '#64748b', marginTop: 3,
            textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}>
            {headerLabel}{crumb && crumb !== headerLabel ? ` / ${crumb}` : ''}
          </div>
        </div>

        {/* Right: global status cluster (env / db / license / user) -- not page-specific */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, height: '100%', paddingLeft: 20, paddingRight: 20, borderLeft: '1px solid #e0e0e0' }}>
          {/* License status -- persistent, dismissible strip (LicenseStatusBanner.tsx), replacing
              RAM-215's full-screen blocking modal per direct product-owner direction (2026-08-08,
              "non-blocking banner"). Renders nothing for local-admin sessions or a genuinely fine
              license -- only occupies space in this cluster when there's actually something to
              say. Mounted once here so it shows on every authenticated page regardless of
              workspace/route. */}
          <LicenseStatusBanner />
          {/* Environment selector -- this UI session's tier, separate from per-connection environments.
              Deliberately the most visually prominent thing in this cluster (size + color-coding by
              stakes, not just another small status pill): every page's data/actions are scoped to
              whichever tier is selected here, so misreading it is the kind of mistake that matters. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 6px 5px 10px', borderRadius: 8,
            backgroundColor: envColors.bg, border: `1.5px solid ${envColors.border}`,
          }}>
            <span style={{ color: envColors.text, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', opacity: 0.75 }}>ENV</span>
            <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', backgroundColor: envColors.dot, flexShrink: 0 }} />
            <select
              value={env}
              onChange={e => setEnvironment(e.target.value as Environment)}
              title="Environment tier for this UI session -- affects Usage reporting and other environment-scoped views. Manage the catalog itself from Settings."
              style={{
                fontWeight: 700, color: envColors.text, background: 'transparent', border: 'none',
                padding: '3px 4px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
              }}
            >
              {(environments ?? []).map(e => (
                <option key={e.name} value={e.name} style={{ color: '#334155' }}>{formatEnvironmentName(e.name)}</option>
              ))}
              {/* Selected env not in the fetched catalog yet (e.g. stale localStorage value from a
                  since-deleted environment, or catalog still loading) -- keep it selectable/visible
                  rather than silently snapping the <select> to whatever's first in the list. */}
              {!(environments ?? []).some(e => e.name === env) && (
                <option value={env} style={{ color: '#334155' }}>{formatEnvironmentName(env)}</option>
              )}
            </select>
          </div>

          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, borderLeft: '1px solid #e2e8f0', paddingLeft: 14 }}>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
                <span style={{ color: '#334155', fontWeight: 600 }}>{user.username}</span>
                {user.tenantRole && (
                  <span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {user.tenantRole}
                  </span>
                )}
              </span>
              <button
                onClick={handleLogout}
                title="Log out"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b',
                  background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, padding: '3px 7px', cursor: 'pointer',
                }}
              >
                <LogOut size={11} /> Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

        {/* Sidebar */}
        <aside style={{
          width: sidebarW, flexShrink: 0, backgroundColor: '#fff',
          borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
          transition: 'width 0.2s', overflowY: 'auto', overflowX: 'hidden',
        }}>

          {/* Collapse/expand toggle -- above the menu it controls, not in the header (which
              keeps a fixed-width logo regardless of sidebar state, see above). */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <button onClick={() => setCollapsed(!collapsed)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 6, display: 'flex' }}
              title="Toggle sidebar"
            >
              <Menu size={16} />
            </button>
          </div>

          {/* Platform Dashboard -- always visible regardless of `workspace` state, deliberately
              ABOVE the workspace switcher (it is not owned by either workspace; see the
              onPlatformDashboard/workspaceLabel logic above). Login and the header logo both
              land here too (Login.tsx's own redirect default, and the logo's onClick above).
              Hidden entirely for a minimal-nav (API-only) session -- it surfaces BI/Blueprint/
              Fabric summaries such a session has no access to anyway; see isMinimalNav's own
              comment above the ADMIN_TENANT_ROLES declaration. */}
          {!isMinimalNav && (
          <div style={{ padding: '10px 8px 0' }}>
            <button
              onClick={() => navigate('/overview')}
              title="Platform Dashboard"
              style={{
                display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                padding: '7px 8px', borderRadius: 7, border: 'none', cursor: 'pointer', textAlign: 'left',
                backgroundColor: onPlatformDashboard ? WORKSPACE_ACTIVE : '#f8fafc',
                color: onPlatformDashboard ? '#fff' : '#475569',
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <LayoutDashboard size={14} />
              {/* RAM-177: match NavSectionBlock's own NavLink capitalization/letter-spacing
                  convention (used by every other nav item in the sidebar) -- this button and the
                  workspace-switcher one below were hand-styled separately and had drifted to plain
                  Title Case while everything else renders uppercase. Size/weight stay distinct
                  (this is deliberately the most prominent item in the sidebar), only the
                  font treatment is unified. */}
              {!collapsed && <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Platform Dashboard</span>}
            </button>
          </div>
          )}

          {/* Workspace switcher -- likewise hidden for a minimal-nav session (neither Fabric nor
              Blueprint applies to an API-only identity). */}
          {!isMinimalNav && (
          <div style={{ padding: '10px 8px 8px', borderBottom: '1px solid #f1f5f9' }}>
            {collapsed ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {([['fabric', Plug], ['blueprint', Rocket]] as [Workspace, React.ElementType][]).map(([w, Icon]) => (
                  <button key={w} title={w === 'fabric' ? 'Runtime Trust Fabric' : 'Blueprint Runtime'}
                    // RAM-158: Fabric's entry point is now Connections, Blueprint's is now
                    // Packages (per-workspace landing pages replacing the old /dashboard,
                    // /blueprint dashboards, which now just redirect to /overview).
                    onClick={() => { setWorkspace(w); navigate(w === 'fabric' ? '/connections' : '/blueprint/packages'); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 7, borderRadius: 7, border: 'none', cursor: 'pointer',
                      backgroundColor: onWorkspaceSection && workspace === w ? WORKSPACE_ACTIVE : '#f8fafc',
                      color: onWorkspaceSection && workspace === w ? '#fff' : '#94a3b8',
                    }}
                  >
                    <Icon size={14} />
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!collapsed && (
                  <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', padding: '0 4px 3px' }}>Workspace</p>
                )}
                {([
                  ['fabric', 'Runtime Trust Fabric', Plug],
                  ['blueprint', 'Blueprint Runtime', Rocket],
                ] as [Workspace, string, React.ElementType][]).map(([w, label, Icon]) => (
                  <button key={w}
                    // RAM-158: Fabric's entry point is now Connections, Blueprint's is now
                    // Packages (per-workspace landing pages replacing the old /dashboard,
                    // /blueprint dashboards, which now just redirect to /overview).
                    onClick={() => { setWorkspace(w); navigate(w === 'fabric' ? '/connections' : '/blueprint/packages'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7,
                      padding: '6px 8px', borderRadius: 7, border: 'none', cursor: 'pointer', textAlign: 'left',
                      backgroundColor: onWorkspaceSection && workspace === w ? WORKSPACE_ACTIVE : '#f8fafc',
                      color: onWorkspaceSection && workspace === w ? '#fff' : '#475569',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Icon size={13} />
                    {/* RAM-177: same font-treatment fix as the Platform Dashboard button above. */}
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Nav */}
          <nav style={{ flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Workspace's own nav section -- only rendered while actually inside that
                workspace's route territory (Platform Dashboard and every COMMON_NAV page
                deliberately render neither FABRIC_NAV nor BLUEPRINT_NAV), and never for a
                minimal-nav session regardless of route. */}
            {onWorkspaceSection && !isMinimalNav && (
              <>
                {!collapsed && (
                  <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', padding: '0 4px 4px' }}>
                    {workspaceLabel}
                  </p>
                )}
                {nav.map((section, sectionIndex) => (
                  <NavSectionBlock key={section.label ?? `section-${sectionIndex}`} section={section} sectionIndex={sectionIndex} collapsed={collapsed} env={env} />
                ))}
              </>
            )}

            {/* Cross-workspace nav -- always rendered, regardless of workspace/page. Each
                section carries its own label (or none, for single flush-item groups); no extra
                umbrella heading above this whole block. A minimal-nav session gets ONLY
                MINIMAL_NAV's single "My API Access" entry here instead -- see isMinimalNav's own
                comment above ADMIN_TENANT_ROLES. */}
            {(isMinimalNav ? MINIMAL_NAV : COMMON_NAV).map((section, sectionIndex) => (
              <NavSectionBlock
                key={section.label ?? `common-section-${sectionIndex}`}
                section={section}
                // +1 when the workspace section rendered above so the first group still gets its
                // top margin instead of visually touching it -- never true for a minimal-nav
                // session since onWorkspaceSection's own block is gated off above too.
                sectionIndex={onWorkspaceSection && !isMinimalNav ? sectionIndex + 1 : sectionIndex}
                collapsed={collapsed}
                env={env}
              />
            ))}
          </nav>

          {!collapsed && (
            <div style={{ padding: '10px 14px', borderTop: '1px solid #e2e8f0', fontSize: 10, color: '#94a3b8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Database size={10} />
                <span>v{data?.version ?? '—'}</span>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', backgroundColor: '#f8fafc', padding: 24 }}>
          <div style={{ maxWidth: 1700, margin: '0 auto' }}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </EnvironmentContext.Provider>
  );
}
